<template>
    <div class="preview-section ppv2-text-black" :style="{ padding: normalizeSize(40, true) }">
        <div :style="{ marginBottom: normalizeSizeVertical(13, true) }">
            <div
                class="d-flex ppv2-opacity-70"
                :style="{ gap: normalizeSize(11), marginBottom: normalizeSizeVertical(20, true) }"
            >
                <Eye :size="15" />
                <span class="preview-text" :style="{ fontSize: normalizeFontSize(12) }">{{ $t('experience.live_preview') }}</span>
            </div>
            <div class="preview-progress">
                <div class="preview-progress-bar" :style="{ width: progressWidth }"></div>
            </div>
        </div>

        <p class="preview-text ppv2-text-black ppv2-opacity-50" :style="{ fontSize: normalizeFontSize(12) }">
            {{ estimatedTime }}
        </p>

        <div v-if="step.id === 2" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <h3
                class="preview-title ppv2-text-black"
                :style="{ fontSize: normalizeFontSize(16) }"
                :class="{ 'show-play': activeField === 'name' }"
            >
                {{ formData.name || $t('experience.step2.content') }}
            </h3>

            <div
                class="ppv2-opacity-80"
                :style="{ fontSize: normalizeFontSize(14) }"
                :class="{ 'show-play': activeField === 'description' }"
                v-html="truncatedDescription"
            ></div>
            <div
                v-if="formData.imagePreview"
                class="preview-image-wrapper"
                :style="{ marginTop: normalizeSizeVertical(20, true) }"
                :class="{ 'show-play': activeField === 'image' }"
            >
                <!-- PDF Icon -->
                <div
                    v-if="isPdfFile"
                    class="pdf-preview-wrapper ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-p-8"
                >
                    <svg
                        width="80"
                        height="80"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="ppv2-text-red-500"
                    >
                        <path
                            d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H13V8H7V10ZM14 2H6C4.89 2 4 2.89 4 4V20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                            fill="currentColor"
                        />
                    </svg>
                    <span class="ppv2-text-sm ppv2-mt-2 ppv2-text-black ppv2-opacity-70">PDF</span>
                </div>
                <!-- Image -->
                <img v-else :src="formData.imagePreview" class="preview-image" />
            </div>

            <div
                v-else
                class="preview-image-wrapper"
                :style="{ marginTop: normalizeSizeVertical(20, true) }"
                :class="{ 'show-play': activeField === 'image' }"
            >
                <img src="/media/bo/previewStep.jpg" class="preview-image" />
            </div>
        </div>
        <div v-if="step.id === 3" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!-- Activities List (Optional, keeping it if needed but styling it minimally to fit) -->
            <div v-if="comboItems.length > 0" :class="{ 'show-play': activeField === 'activities' }">
                <div v-for="(item, index) in comboItems" :key="index" class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mb-2">
                    <span class="ppv2-text-sm ppv2-text-slate-600">
                        {{ item.selectedActivity?.name || `Activité ${index + 1}` }}
                        <span v-if="item.selectedDuration"> - {{ item.selectedDuration.duration }} min </span>
                    </span>
                </div>
            </div>
        </div>
        <div v-if="step.id === 4" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!-- Pricing Information -->
            <h3
                v-if="pricing.price"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'price' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.prix', { price: pricing.price + $symbolCurrency }) }}
                <span v-if="pricing.pricing_type === 'per_person'" class="poppinsRegular">
                    {{ $t('package.step4.pricingType.per_person_suffix') }}
                </span>
            </h3>
            <h3
                v-if="deposite.enabled && acompteDisplay"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'deposite' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.acompte_to_pay', { acompte: acompteDisplay }) }}
            </h3>
        </div>
        <div v-if="step.id === 5" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!-- Selection Rules -->
            <h3
                v-if="parameters.item_qty_min || parameters.item_qty_max"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'selectionRules' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.rules_selection', { min: parameters.item_qty_min || 0, max: parameters.item_qty_max || 0 }) }}
            </h3>
            <h3
                v-if="parameters.slot_succession"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'breakTime' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.delay_between_activities', { delay: parameters.slot_succession }) }}
            </h3>
        </div>
        <div v-if="step.id === 6" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!-- Category -->
            <div v-if="selectedTag" :class="{ 'show-play': activeField === 'categories' }">
                <h3 class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('experience.category_selected') }}
                </h3>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-gap-2 ppv2-mt-2">
                    <span class="ppv2-px-3 ppv2-py-1 ppv2-bg-purple-100 ppv2-text-purple-800 ppv2-rounded-full ppv2-text-sm">
                        {{ selectedTag.name }}
                    </span>
                </div>
            </div>
        </div>
        <img src="/media/bo/icons/leftWrapper.svg" class="preview-image rounded-0 ppv2-pt-6" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Eye } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'PreviewPackage',
    components: { Eye },
    props: {
        activeField: {
            type: String,
            default: null,
        },
        step: {
            type: Object,
            default: () => ({}),
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
    },
    computed: {
        ...mapGetters('package', ['formData', 'comboItems', 'pricing', 'deposite', 'parameters', 'selectedTag', 'currentStep']),
        isPdfFile() {
            const preview = this.formData?.imagePreview
            if (!preview) return false
            if (typeof preview === 'string') {
                return preview.toLowerCase().includes('data:application/pdf;base64')
            }
            return false
        },
        progressWidth() {
            const totalSteps = 6
            const currentStep = this.$store.getters['package/currentStep'] || 1
            return `${(currentStep / totalSteps) * 100}%`
        },
        estimatedTime() {
            const totalMinutes = this.comboItems.reduce((total, item) => {
                return total + (item.selectedDuration?.duration || 0)
            }, 0)

            if (totalMinutes === 0) {
                return this.$t('experience.estimated_time', { time: '--' })
            }

            if (totalMinutes >= 60) {
                const hours = Math.floor(totalMinutes / 60)
                const mins = totalMinutes % 60
                return `Temps total estimé : ~${hours}h${mins > 0 ? mins : ''}`
            }

            return `Temps total estimé : ~${totalMinutes} minutes`
        },
        truncatedDescription() {
            const desc = this.formData?.description || 'Description de votre formule'
            // Supprime les balises HTML si tu veux limiter par texte pur
            const plainText = desc.replace(/<[^>]+>/g, '')
            if (plainText.length > 500) {
                return plainText.substring(0, 500) + '...'
            }
            return desc
        },
        acompteDisplay() {
            if (!this.deposite.enabled) return ''
            const { type, value } = this.deposite
            if (!value || value === null || value === undefined) return ''
            if (type === 'percentage') {
                return `${value}%`
            } else if (type === 'amount') {
                return `${value}${this.$symbolCurrency}`
            }
            return ''
        },
    },
}
</script>

<style scoped>
.preview-section {
    flex: 0.6;
    background: #d9d9d933;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.dark .preview-section {
    background: #1b194c;
}

.preview-text {
    font-family: 'Poppins Light', sans-serif;
}

.preview-progress {
    flex: 1;
    height: 7px;
    background: #9b9b9b;
    border-radius: 3px;
}

.preview-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #3a329d 27%, #8241d7 100%);
    border-radius: 3px;
    transition: width 0.3s ease;
}

.preview-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preview-title {
    font-family: 'Poppins Regular', sans-serif;
    font-weight: 400;
}

.preview-image-wrapper {
    width: 100%;
    height: auto;
    overflow: hidden;
    border-radius: 12px;
}

.preview-image {
    width: 100%;
    height: auto;
    border-radius: 12px;
    object-fit: cover;
}

.preview-content .show-play {
    position: relative;
}

.preview-content .show-play::before {
    content: '';
    position: absolute;
    left: -24px;
    top: 7px;
    width: 12px;
    height: 14px;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='%236C69FF' viewBox='0 0 24 24'><polygon points='8,5 19,12 8,19'/></svg>")
        no-repeat center;
    background-size: contain;
    opacity: 1;
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.dark .preview-content .show-play::before {
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='%237D2DBD' viewBox='0 0 24 24'><polygon points='8,5 19,12 8,19'/></svg>")
        no-repeat center;
}

.preview-content .show-play:hover::before,
.preview-content .show-play::before {
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
}
</style>
